import { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogActions, Divider, Link } from "@mui/material";
import IconButton from "@mui/material/IconButton";

import { useSnackbar } from "../../Components/SharedSnackbar/SharedSnackbar.context";
import { essentialsEscalatePrice } from "../../constants";
import mixpanel from "../../utils/mixpanel";
import TicketEscalation from "./EscalateStepper/TicketEscalation";

type Props = {
  setBillingProfile: (profile: string) => void;
  billingProfile: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const TermsAgreementDialog = ({ billingProfile, setBillingProfile, open, setOpen }: Props) => {
  const { onOpen: showSnackbar, onClose: hideSnackbar } = useSnackbar();
  const [hasAgreedToCosts, setCostChecked] = useState<boolean>(false);
  const [isAuthorizedToMakeDecisions, setAuthorizedChecked] = useState<boolean>(false);
  const [hasAgreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [selectedBillingProfile, setSelectedBillingProfile] = useState<string>(billingProfile);

  const { t } = useTranslation("services");

  const hasConfirmed = hasAgreedToCosts && isAuthorizedToMakeDecisions && hasAgreedToTerms && selectedBillingProfile;

  const onClose = useCallback(() => {
    setOpen(false);
    setBillingProfile("");
  }, [setBillingProfile, setOpen]);

  const handleConfirm = () => {
    if (hasConfirmed) {
      showSnackbar({
        message: "You have accepted the terms and conditions. You may escalate your ticket.",
        variant: "success",
        autoHideDuration: 5000,
        action: [
          <IconButton key="close" aria-label="Close" color="inherit" onClick={hideSnackbar} size="large">
            <CloseIcon />
          </IconButton>,
        ],
      });
      mixpanel.track("ava.essentials.escalate.confirm-payment", {
        flow: "ava.essentials.open-ticket",
      });
      setOpen(false);
      setBillingProfile(selectedBillingProfile);
    }
  };

  return (
    <Box>
      <Alert
        severity="info"
        sx={{ ".MuiAlert-action": { alignItems: "center", minWidth: "fit-content" }, mt: 2 }}
        action={
          <Button
            variant="text"
            onClick={() => {
              setOpen(true);
            }}
          >
            {t("ProductStep.dciEssentialsAlertAction")}
          </Button>
        }
      >
        {t("ProductStep.dciEssentialsAlert", { price: essentialsEscalatePrice })}{" "}
        <Link
          href="https://help.doit.com/docs/services/consulting-support/create-support-request"
          target="_blank"
          sx={{ textDecoration: "none" }}
        >
          Learn more
        </Link>
        .
      </Alert>

      <Dialog open={open} onClose={onClose} maxWidth="md" slotProps={{ paper: { sx: { pt: 2 } } }}>
        <TicketEscalation
          billingProfile={selectedBillingProfile}
          setBillingProfile={setSelectedBillingProfile}
          hasAgreedToCosts={hasAgreedToCosts}
          setCostChecked={setCostChecked}
          isAuthorizedToMakeDecisions={isAuthorizedToMakeDecisions}
          setAuthorizedChecked={setAuthorizedChecked}
          hasAgreedToTerms={hasAgreedToTerms}
          setAgreedToTerms={setAgreedToTerms}
        />
        <Divider />
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" disabled={!hasConfirmed}>
            Confirm payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
